import styles from "./AssetPanel.module.scss";
import DefaultTemplatePanel from "../../layout/content-templates/default/DefaultTemplatePanel.tsx";
import { ComponentPropsWithoutRef, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import AssetDetails from "./AssetDetails.tsx";
import { PanelType } from "./types.ts";
import AssetResources from "./AssetResources.tsx";
import AssetPanelHeader from "./AssetPanelHeader.tsx";
import TabGroup from "common/core-ui/TabGroup.tsx";
import {
  License,
  ManageAsset,
} from "../../../../common/api/decimal/common-types/manageAssets.ts";
import AssetAccessibility from "./AssetAccessibility.tsx";

interface AssetPanelProps
  extends Omit<
    ComponentPropsWithoutRef<typeof DefaultTemplatePanel>,
    "children"
  > {
  asset?: ManageAsset;
  license?: License;
  explore?: VST.Bridge.Asset;
  startTab?: PanelType;
}

/**
 * Only works in views that use the DefaultTemplate
 */
function InternalAssetPanel({
  asset,
  license,
  explore,
  startTab,
}: Pick<AssetPanelProps, "asset" | "license" | "explore" | "startTab">) {
  const intl = useIntl();

  const tabs = useMemo(() => {
    const tabslist = [
      {
        label: intl.formatMessage({ defaultMessage: "Details" }),
        panel: (
          <AssetDetails asset={asset} license={license} explore={explore} />
        ),
        type: PanelType.Details,
        enabled: true,
      },
      {
        label: intl.formatMessage({ defaultMessage: "Accessibility" }),
        panel: <AssetAccessibility asset={asset} explore={explore} />,
        type: PanelType.Accessibility,
        enabled: true,
      },
    ];

    if (asset) {
      tabslist.push({
        label: intl.formatMessage({ defaultMessage: "Ancillaries" }),
        panel: <AssetResources resources={asset.ancillaries} />,
        type: PanelType.Ancillaries,
        enabled: asset && asset.ancillaries.length > 0,
      });
    }

    return tabslist.filter((el) => el.enabled);
  }, [asset, license, explore, intl]);

  const [tabIndex, setTabIndex] = useState(
    startTab ? tabs.findIndex((tab) => tab.type === startTab) : 0
  );

  return (
    <>
      <AssetPanelHeader asset={asset} license={license} explore={explore} />
      <div className={styles.bottomArea}>
        <TabGroup
          tabs={tabs}
          selectedIndex={tabIndex}
          onSelect={(index) => setTabIndex(index)}
        ></TabGroup>
      </div>
    </>
  );
}

export default function AssetPanel({
  asset,
  license,
  explore,
  startTab,
  ...wrapperProps
}: AssetPanelProps) {
  return (
    <DefaultTemplatePanel {...wrapperProps} className={styles.outerPanel}>
      <InternalAssetPanel
        asset={asset}
        license={license}
        explore={explore}
        startTab={startTab}
      />
    </DefaultTemplatePanel>
  );
}
